import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  LinearProgress,
  Alert,
  Divider,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import ImportStatusBadge from './ImportStatusBadge';
import { useStatusPolling } from '../Hooks/useStatusPolling';
import { InvoiceImportStatus } from '../Types/invoiceImport.types';
import { getErrorMessage } from '../Utils/errorCodeMapping';
import { useNotification } from '../../Invoices/Context/NotificationContext';

/** OCR confidence is always 0..1 (RM-06); mirrors the backend's own 0.70 validation threshold. */
const OCR_CONFIDENCE_WARNING_THRESHOLD = 0.7;

interface ImportProgressTrackerProps {
  /** Import job ID to track */
  importJobId: number;
  /** Auto-redirect to invoice on completion */
  autoRedirect?: boolean;
  /** Redirect delay in milliseconds (default: 2000ms) */
  redirectDelayMs?: number;
  /** Callback when status changes */
  onStatusChange?: (status: InvoiceImportStatus) => void;
}

/**
 * Progress tracker component with auto-polling
 * Displays real-time import status, warnings, errors, and metadata with toast notifications
 */
const ImportProgressTracker: React.FC<ImportProgressTrackerProps> = ({
  importJobId,
  autoRedirect = true,
  redirectDelayMs = 2000,
  onStatusChange,
}) => {
  const navigate = useNavigate();
  const { showSuccess } = useNotification();
  const hasRedirectedRef = useRef(false);

  // Auto-polling hook
  const {
    status: importStatus,
    isLoading,
    isFetching,
    error,
    retryCount,
    currentInterval,
    isTerminal,
    refetch,
  } = useStatusPolling(importJobId);

  // Notify parent on status change
  useEffect(() => {
    if (importStatus && onStatusChange) {
      onStatusChange(importStatus.status);
    }
  }, [importStatus, onStatusChange]);

  // Auto-redirect on completion or requires review
  useEffect(() => {
    if (!autoRedirect || !importStatus || hasRedirectedRef.current) return;

    // Redirect on RequiresReview - go to import job detail page
    if (importStatus.status === InvoiceImportStatus.RequiresReview) {
      hasRedirectedRef.current = true;
      showSuccess(`Import zakończony, faktura wymaga weryfikacji. Przekierowanie do szczegółów...`);
      const timer = setTimeout(() => {
        navigate(`/expense-invoices/import/${importJobId}`);
      }, redirectDelayMs);
      return () => clearTimeout(timer);
    }

    // Redirect on Completed
    if (importStatus.status === InvoiceImportStatus.Completed) {
      console.log('🚀 Auto-redirect triggered for Completed status');
      console.log('🚀 expenseInvoiceId:', importStatus.expenseInvoiceId);
      console.log('🚀 invoiceId:', importStatus.invoiceId);

      hasRedirectedRef.current = true;
      // Priority 1: Expense invoice
      if (importStatus.expenseInvoiceId) {
        showSuccess(
          `Faktura kosztowa ${
            importStatus.invoiceNumber || ''
          } została pomyślnie zaimportowana! Przekierowanie...`
        );
        const timer = setTimeout(() => {
          navigate(`/expense-invoices/${importStatus.expenseInvoiceId}`);
        }, redirectDelayMs);
        return () => clearTimeout(timer);
      }
      // Priority 2: Sales invoice (current behavior)
      else if (importStatus.invoiceId) {
        showSuccess(
          `Faktura ${
            importStatus.invoiceNumber || ''
          } została pomyślnie zaimportowana! Przekierowanie...`
        );
        const timer = setTimeout(() => {
          navigate(`/invoices/${importStatus.invoiceId}`);
        }, redirectDelayMs);
        return () => clearTimeout(timer);
      }
    }
  }, [autoRedirect, importStatus, importJobId, navigate, redirectDelayMs, showSuccess]);

  // Loading state
  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Pobieranie statusu importu...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Ponów
            </Button>
          }
        >
          Błąd pobierania statusu importu. Sprawdź połączenie sieciowe.
        </Alert>
      </Paper>
    );
  }

  // No data
  if (!importStatus) {
    return null;
  }

  const {
    status,
    statusLabel,
    fileName,
    fileSizeBytes,
    importerType,
    ocrConfidence,
    processingDurationMs,
    warnings,
    issues,
    errorMessage,
    errorCode,
    invoiceId,
    invoiceNumber,
    existingInvoiceId,
    startedAt,
    completedAt,
  } = importStatus;

  // Confidence is always on the backend's 0..1 scale (RM-06: this scale has drifted before).
  // OCR_CONFIDENCE_WARNING_THRESHOLD stays in that same scale so every comparison below — the
  // progress bar color, the low-quality caption, and the RequiresReview alert — agrees.
  const hasOcrConfidence = typeof ocrConfidence === 'number' && !Number.isNaN(ocrConfidence);
  const ocrConfidencePct: number | null = hasOcrConfidence ? ocrConfidence * 100 : null;
  const warningMessages =
    issues && issues.length > 0
      ? Array.from(new Set(issues.map((issue) => issue.message)))
      : warnings && warnings.length > 0
        ? ['Import zawiera ostrzeżenia wymagające ręcznej weryfikacji.']
        : [];

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header with status badge */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Status importu</Typography>
          <ImportStatusBadge status={status} statusLabel={statusLabel} />
        </Stack>

        <Divider />

        {/* File info */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Plik
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>Nazwa:</strong> {fileName}
            </Typography>
            <Typography variant="body2">
              <strong>Rozmiar:</strong> {formatFileSize(fileSizeBytes)}
            </Typography>
            {importerType && (
              <Typography variant="body2">
                <strong>Importer:</strong> {importerType}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Processing progress */}
        {status === InvoiceImportStatus.Processing && (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Przetwarzanie...
              </Typography>
              {isFetching && <CircularProgress size={16} />}
            </Stack>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Odświeżanie co {currentInterval / 1000}s
              {retryCount > 0 && ` (próba ${retryCount + 1})`}
            </Typography>
          </Box>
        )}

        {/* Timing info */}
        {(startedAt || completedAt || processingDurationMs) && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Czas przetwarzania
            </Typography>
            <Stack spacing={0.5}>
              {startedAt && (
                <Typography variant="body2">
                  <strong>Rozpoczęto:</strong> {new Date(startedAt).toLocaleString('pl-PL')}
                </Typography>
              )}
              {completedAt && (
                <Typography variant="body2">
                  <strong>Zakończono:</strong> {new Date(completedAt).toLocaleString('pl-PL')}
                </Typography>
              )}
              {processingDurationMs && (
                <Typography variant="body2">
                  <strong>Czas trwania:</strong> {formatDuration(processingDurationMs)}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* OCR confidence */}
        {ocrConfidencePct !== null && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Jakość rozpoznania tekstu (OCR)
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <LinearProgress
                variant="determinate"
                value={ocrConfidencePct}
                sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                color={
                  ocrConfidence !== null && ocrConfidence >= OCR_CONFIDENCE_WARNING_THRESHOLD
                    ? 'success'
                    : 'warning'
                }
              />
              <Typography variant="body2" fontWeight="medium">
                {ocrConfidencePct.toFixed(0)}%
              </Typography>
            </Stack>
            {ocrConfidence !== null && ocrConfidence < OCR_CONFIDENCE_WARNING_THRESHOLD && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                Niska jakość rozpoznania - może wymagać weryfikacji
              </Typography>
            )}
          </Box>
        )}

        {/* Warnings */}
        {warningMessages.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ostrzeżenia
            </Typography>
            <List dense disablePadding>
              {warningMessages.map((warning, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <WarningIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText primary={warning} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Success - Completed */}
        {status === InvoiceImportStatus.Completed && (
          <Alert
            severity="success"
            icon={<CheckIcon />}
            action={
              (invoiceId || importStatus.expenseInvoiceId) && (
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<ViewIcon />}
                  onClick={() => {
                    if (importStatus.expenseInvoiceId) {
                      navigate(`/expense-invoices/${importStatus.expenseInvoiceId}`);
                    } else if (invoiceId) {
                      navigate(`/invoices/${invoiceId}`);
                    }
                  }}
                >
                  Zobacz fakturę
                </Button>
              )
            }
          >
            Import zakończony sukcesem!{' '}
            {invoiceNumber && (
              <>
                Faktura <strong>{invoiceNumber}</strong> została utworzona.
              </>
            )}
            {autoRedirect && ' Przekierowanie za chwilę...'}
          </Alert>
        )}

        {/* Info - Duplicate */}
        {status === InvoiceImportStatus.Duplicate && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            action={
              existingInvoiceId || importStatus.existingExpenseInvoiceId ? (
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<ViewIcon />}
                  onClick={() => {
                    if (importStatus.existingExpenseInvoiceId) {
                      navigate(`/expense-invoices/${importStatus.existingExpenseInvoiceId}`);
                    } else if (existingInvoiceId) {
                      navigate(`/invoices/${existingInvoiceId}`);
                    }
                  }}
                >
                  Zobacz fakturę
                </Button>
              ) : undefined
            }
          >
            {errorMessage ||
              'Faktura o tym numerze lub plik o tej zawartości już istnieje w systemie.'}
          </Alert>
        )}

        {/* Warning - RequiresReview */}
        {status === InvoiceImportStatus.RequiresReview && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<ViewIcon />}
                onClick={() => navigate(`/expense-invoices/import/${importJobId}`)}
              >
                Weryfikuj dane
              </Button>
            }
          >
            Import zakończony, ale faktura wymaga ręcznej weryfikacji
            {ocrConfidence !== null &&
              ocrConfidence < OCR_CONFIDENCE_WARNING_THRESHOLD &&
              ' (niska jakość OCR)'}
            . Sprawdź dane
            przed zatwierdzeniem.
            {autoRedirect && ' Przekierowanie za chwilę...'}
          </Alert>
        )}

        {/* Error - Failed */}
        {status === InvoiceImportStatus.Failed && (
          <Alert
            severity="error"
            icon={<ErrorIcon />}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
              >
                Spróbuj ponownie
              </Button>
            }
          >
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Import nie powiódł się
            </Typography>
            <Typography variant="body2">
              {errorCode ? getErrorMessage(errorCode) : errorMessage || 'Nieznany błąd'}
            </Typography>
          </Alert>
        )}

        {/* Debug info (terminal status) */}
        {isTerminal && (
          <Typography variant="caption" color="text.secondary" align="center">
            Status końcowy - polling zatrzymany
          </Typography>
        )}

        {/* Fallback action buttons for terminal statuses */}
        {isTerminal && (
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/expense-invoices')}
              startIcon={<ViewIcon />}
            >
              Powrót do listy faktur
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/expense-invoices/import/history')}
              startIcon={<ViewIcon />}
            >
              Historia importu
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default ImportProgressTracker;
